import {
  ZadProcurementAttachment,
  ZadProcurementConversion,
  ZadProcurementDocument,
  ZadProcurementDocumentType,
  ZadProcurementLine,
  ZadProcurementLineType,
  ZadProcurementLineWritePayload,
  ZadProcurementPrintDto,
  ZadProcurementQuote,
  ZadProcurementQuotePayload,
  ZadProcurementRecipientMode,
  ZadProcurementRecipientSnapshot,
  ZadProcurementStatus,
  ZadProcurementWritePayload,
  ZadProduct,
  ZadPurchaseSupplierRef,
  ZadReplenishmentCoverageStatus,
  ZadReplenishmentPhysicalStatus,
  ZadReplenishmentRow,
  ZadReplenishmentSelectionItem,
  ZadIssuerProfile,
  ZadSupplier,
} from '../../../models/zad'
import {resolveZadUserMessage, ZadApiError} from './errors'
import {parseZadPriceInput, roundZadMoney3} from './productHelpers'
import {isZadReplenishmentSelectable, ZadReplenishmentQuery} from './replenishmentHelpers'

/**
 * RFQ never contributes to onOrderQuantity.
 * DRAFT purchase orders do not reserve stock.
 * CONFIRMED/SENT purchase orders contribute to expected supply.
 */

export const ZAD_PROCUREMENT_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024
export const ZAD_PROCUREMENT_MAX_ATTACHMENTS = 10
export const ZAD_PROCUREMENT_ATTACHMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp']
export const ZAD_PROCUREMENT_ATTACHMENT_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
]

export interface ZadProcurementQuery {
  search?: string
  documentType?: string
  status?: string
  supplier?: string
  recipientMode?: string
  from?: string
  to?: string
  pageNumber: number
  pageCount: number
  sortField?: string
  sortOrder?: string
}

export const DEFAULT_ZAD_PROCUREMENT_QUERY: ZadProcurementQuery = {
  search: '',
  documentType: '',
  status: '',
  supplier: '',
  recipientMode: '',
  from: '',
  to: '',
  pageNumber: 1,
  pageCount: 10,
  sortField: 'createdAt',
  sortOrder: 'desc',
}

export interface ZadProcurementLineReplenishmentContext {
  currentStock: number
  onOrderQuantity: number
  projectedStock: number
  targetStock: number | null
  remainingToOrder: number
  status?: ZadReplenishmentPhysicalStatus
  coverageStatus?: ZadReplenishmentCoverageStatus
}

export interface ZadProcurementEditorLine {
  clientId: string
  lineId?: string
  lineType: ZadProcurementLineType
  productId?: string
  description: string
  reference: string
  barcode?: string
  quantity: string
  unitPrice: string
  /** Read-only current purchase price. Never written back as unitPrice. */
  previousPurchasePrice?: number | null
  note: string
  fromReplenishmentSuggestion?: boolean
  replenishment?: ZadProcurementLineReplenishmentContext
}

export interface NormalizedProcurementLine {
  lineType: ZadProcurementLineType
  productId?: string
  productName: string
  productCode: string
  barcode: string
  description: string
  reference: string
  quantity: number
  unitPrice: number | null
  quotedUnitPrice: number | null
  note: string
  previousPurchasePrice: number | null
}

export interface ZadProcurementEditorValues {
  documentType: ZadProcurementDocumentType
  recipientMode: ZadProcurementRecipientMode
  supplier: string
  recipientName: string
  recipientAddress: string
  recipientCity: string
  recipientPhone: string
  recipientEmail: string
  recipientTaxId: string
  supplierNote: string
  internalNote: string
  items: ZadProcurementEditorLine[]
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function firstDefined(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined && value !== null)
}

function asText(value: unknown): string {
  return value == null ? '' : String(value).trim()
}

function asFiniteNumber(value: unknown): number | undefined {
  if (value === '' || value == null) return undefined
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}

function asNumberOrZero(value: unknown): number {
  return asFiniteNumber(value) ?? 0
}

function asNullableNumber(value: unknown): number | null {
  if (value === '' || value == null) return null
  const numeric = asFiniteNumber(value)
  return numeric === undefined ? null : numeric
}

function asDocumentType(value: unknown): ZadProcurementDocumentType {
  const text = asText(value).toUpperCase()
  if (text === 'PURCHASE_ORDER' || text === 'PO' || text === 'BC') return 'PURCHASE_ORDER'
  return 'RFQ'
}

export function isManualRecipientMode(value?: string | null): boolean {
  const text = asText(value).toUpperCase()
  return (
    text === 'MANUAL_RECIPIENT' ||
    text === 'MANUAL' ||
    text === 'FREE_TEXT' ||
    text === 'ADHOC'
  )
}

function asRecipientMode(value: unknown): ZadProcurementRecipientMode {
  return isManualRecipientMode(asText(value)) ? 'MANUAL_RECIPIENT' : 'REGISTERED_SUPPLIER'
}

function asLineType(value: unknown): ZadProcurementLineType {
  const text = asText(value).toUpperCase()
  if (text === 'MANUAL' || text === 'FREE_TEXT' || text === 'CUSTOM') return 'MANUAL'
  return 'PRODUCT'
}

function asStatus(value: unknown): ZadProcurementStatus {
  const text = asText(value).toUpperCase()
  if (
    text === 'DRAFT' ||
    text === 'SENT' ||
    text === 'QUOTED' ||
    text === 'CONFIRMED' ||
    text === 'CANCELLED'
  ) {
    return text
  }
  return 'DRAFT'
}

export function buildZadProcurementQuery(params: ZadProcurementQuery): string {
  const query = new URLSearchParams()
  query.set('pageNumber', String(params.pageNumber > 0 ? params.pageNumber : 1))
  query.set('pageCount', String(params.pageCount > 0 ? params.pageCount : 10))
  query.set('sortField', params.sortField?.trim() || 'createdAt')
  query.set('sortOrder', params.sortOrder === 'asc' ? 'asc' : 'desc')
  if (params.search?.trim()) query.set('search', params.search.trim())
  if (params.documentType?.trim()) query.set('documentType', params.documentType.trim())
  if (params.status?.trim()) query.set('status', params.status.trim())
  if (params.supplier?.trim()) query.set('supplier', params.supplier.trim())
  if (params.recipientMode?.trim()) query.set('recipientMode', params.recipientMode.trim())
  if (params.from?.trim()) query.set('from', params.from.trim())
  if (params.to?.trim()) query.set('to', params.to.trim())
  return query.toString()
}

function extractSupplierRef(value: unknown): ZadPurchaseSupplierRef | string | null {
  if (!value) return null
  if (typeof value === 'string') return value
  const raw = asRecord(value)
  const id = asText(firstDefined(raw._id, raw.id))
  if (!id) return null
  return {
    _id: id,
    name: asText(raw.name),
    code: asText(raw.code),
    phone: asText(raw.phone) || undefined,
    email: asText(raw.email) || undefined,
    isActive: raw.isActive !== false,
  }
}

export function getZadProcurementSupplierId(
  supplier: ZadProcurementDocument['supplier']
): string {
  if (!supplier) return ''
  if (typeof supplier === 'string') return supplier
  return supplier._id || ''
}

export function getZadProcurementSupplierLabel(document: ZadProcurementDocument): string {
  if (isManualRecipientMode(document.recipientMode)) {
    return document.recipientSnapshot.name || '—'
  }
  const supplier = document.supplier
  if (!supplier) return document.recipientSnapshot.name || '—'
  if (typeof supplier === 'string') return document.recipientSnapshot.name || supplier
  const name = supplier.name || document.recipientSnapshot.name
  return supplier.code ? `${name} — ${supplier.code}` : name || '—'
}

function extractRecipientSnapshot(value: unknown, supplier: unknown): ZadProcurementRecipientSnapshot {
  const raw = asRecord(value)
  const supplierRaw = asRecord(supplier)
  return {
    name: asText(firstDefined(raw.name, supplierRaw.name)),
    address: asText(firstDefined(raw.address, supplierRaw.address)) || undefined,
    city: asText(firstDefined(raw.city, supplierRaw.city)) || undefined,
    phone: asText(firstDefined(raw.phone, supplierRaw.phone)) || undefined,
    email: asText(firstDefined(raw.email, supplierRaw.email)) || undefined,
    taxId: asText(firstDefined(raw.taxId, raw.fiscalId, supplierRaw.taxId)) || undefined,
  }
}

function scalarText(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

function objectRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function entityId(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') return scalarText(value)
  const record = objectRecord(value)
  if (!record) return ''
  return scalarText(firstDefined(record._id, record.id))
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    const text = scalarText(value)
    if (text) return text
  }
  return ''
}

/**
 * Product identity comes from the procurement line itself.
 * A populated `productId` or `product` object is read as an object, never stringified.
 * Current Product.purchasePrice is kept separate from the editable supplier unitPrice.
 */
export function normalizeProcurementLine(value: unknown): NormalizedProcurementLine | null {
  const raw = asRecord(value)
  const snapshot = objectRecord(firstDefined(raw.productSnapshot, raw.snapshot))
  const populated = objectRecord(raw.product) || objectRecord(raw.productId)
  const declared = asLineType(firstDefined(raw.lineType, raw.type))
  const productId = entityId(raw.productId) || entityId(raw.product)
  const lineType: ZadProcurementLineType =
    declared === 'MANUAL' ? 'MANUAL' : productId ? 'PRODUCT' : declared

  const snapshotName = firstText(snapshot?.name, snapshot?.productName)
  const snapshotCode = firstText(snapshot?.code, snapshot?.productCode)
  const snapshotBarcode = firstText(snapshot?.barcode)
  const populatedName = firstText(populated?.name, populated?.productName)
  const populatedCode = firstText(populated?.code, populated?.productCode)
  const populatedBarcode = firstText(populated?.barcode)
  const aliasName = firstText(raw.productName, raw.description, raw.designation, raw.name)
  const aliasCode = firstText(raw.productCode, raw.reference, raw.code)
  const aliasBarcode = firstText(raw.barcode)

  const productName = snapshotName || populatedName || aliasName
  const productCode = snapshotCode || populatedCode || aliasCode
  const barcode = snapshotBarcode || populatedBarcode || aliasBarcode
  const description = lineType === 'MANUAL' ? aliasName : productName
  const reference = lineType === 'MANUAL' ? aliasCode : productCode
  const quantity = asNumberOrZero(raw.quantity)
  if (!description && !reference && !productId && quantity <= 0) return null

  const persistedUnitPrice = asNullableNumber(raw.unitPrice)
  const quotedUnitPrice = asNullableNumber(firstDefined(raw.quotedUnitPrice, raw.quotePrice))
  const legacyPrice = asNullableNumber(raw.price)
  const unitPrice =
    persistedUnitPrice != null ? persistedUnitPrice : quotedUnitPrice != null ? quotedUnitPrice : legacyPrice
  const previousPurchasePrice =
    lineType === 'PRODUCT' ? asNullableNumber(populated?.purchasePrice) : null

  return {
    lineType,
    productId: lineType === 'PRODUCT' ? productId || undefined : undefined,
    productName: lineType === 'PRODUCT' ? productName : '',
    productCode: lineType === 'PRODUCT' ? productCode : '',
    barcode,
    description,
    reference,
    quantity,
    unitPrice,
    quotedUnitPrice,
    note: scalarText(firstDefined(raw.note, raw.specification, raw.lineNote)),
    previousPurchasePrice,
  }
}

function extractLine(value: unknown): ZadProcurementLine | null {
  const raw = asRecord(value)
  const line = normalizeProcurementLine(value)
  if (!line) return null
  const subtotalRaw = asNullableNumber(firstDefined(raw.subtotal, raw.lineSubtotal, raw.amount))
  return {
    _id: scalarText(firstDefined(raw._id, raw.id, raw.lineId)) || undefined,
    lineType: line.lineType,
    productId: line.productId,
    description: line.description,
    reference: line.reference,
    barcode: line.barcode || undefined,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    previousPurchasePrice: line.previousPurchasePrice,
    subtotal:
      subtotalRaw != null
        ? subtotalRaw
        : line.unitPrice != null
        ? roundZadMoney3(line.quantity * line.unitPrice)
        : null,
    note: line.note,
  }
}

function extractAttachment(value: unknown): ZadProcurementAttachment | null {
  const raw = asRecord(value)
  const id = asText(firstDefined(raw._id, raw.id, raw.attachmentId))
  if (!id) return null
  return {
    _id: id,
    fileName: asText(firstDefined(raw.fileName, raw.filename, raw.originalName, raw.name)),
    mimeType: asText(firstDefined(raw.mimeType, raw.contentType, raw.type)),
    size: asNumberOrZero(firstDefined(raw.size, raw.bytes)),
    createdAt: asText(firstDefined(raw.createdAt, raw.uploadedAt)) || undefined,
    isActive: raw.isActive !== false && raw.active !== false,
  }
}

function extractQuote(value: unknown): ZadProcurementQuote | null {
  if (!value || typeof value !== 'object') return null
  const raw = asRecord(value)
  const linesRaw = firstDefined(raw.lines, raw.items)
  return {
    quoteReference: asText(firstDefined(raw.quoteReference, raw.reference, raw.number)),
    quoteDate: asText(firstDefined(raw.quoteDate, raw.date)),
    lines: Array.isArray(linesRaw)
      ? linesRaw.map((line) => {
          const item = asRecord(line)
          return {
            lineId: asText(firstDefined(item.lineId, item._id, item.id)) || undefined,
            productId: asText(item.productId) || undefined,
            description: asText(firstDefined(item.description, item.designation)) || undefined,
            quantity: asFiniteNumber(item.quantity),
            unitPrice: asNumberOrZero(item.unitPrice),
          }
        })
      : [],
  }
}

function extractConversion(value: unknown): ZadProcurementConversion | null {
  if (!value || typeof value !== 'object') return null
  const raw = asRecord(value)
  const purchase = asRecord(raw.purchase)
  const purchaseId = asText(firstDefined(raw.purchaseId, purchase._id, raw.purchase))
  const purchaseNumber = asText(
    firstDefined(raw.purchaseNumber, purchase.purchaseNumber, purchase.number)
  )
  const isConverted =
    raw.isConverted === true || Boolean(purchaseId || purchaseNumber)
  if (!isConverted && !purchaseId && !purchaseNumber) return null
  return {
    isConverted,
    purchaseId: purchaseId || undefined,
    purchaseNumber: purchaseNumber || undefined,
    purchaseStatus: asText(firstDefined(raw.purchaseStatus, purchase.status)) || undefined,
    convertedAt: asText(firstDefined(raw.convertedAt, purchase.convertedAt, purchase.createdAt)) || undefined,
  }
}

export function extractZadProcurementConvertResult(data: unknown): {
  document: ZadProcurementDocument | null
  conversion: ZadProcurementConversion | null
} {
  const document = extractZadProcurementDocument(data)
  const raw = asRecord(data)
  const inner = asRecord(raw.data)
  const purchase = firstDefined(raw.purchase, inner.purchase)
  const purchaseId = firstDefined(raw.purchaseId, inner.purchaseId)
  const purchaseNumber = firstDefined(raw.purchaseNumber, inner.purchaseNumber)
  const conversion =
    document?.conversion ||
    extractConversion(firstDefined(raw.conversion, inner.conversion)) ||
    (purchase || purchaseId || purchaseNumber
      ? extractConversion({
          isConverted: true,
          purchase,
          purchaseId,
          purchaseNumber,
          purchaseStatus: firstDefined(raw.purchaseStatus, inner.purchaseStatus),
          convertedAt: firstDefined(raw.convertedAt, inner.convertedAt),
        })
      : null)
  return {document, conversion: conversion?.isConverted ? conversion : null}
}

export function extractConversionFromApiError(err: ZadApiError): ZadProcurementConversion | null {
  const details = asRecord(err.details)
  const conversion =
    extractConversion(details) ||
    extractConversion({
      isConverted: true,
      purchase: details.purchase,
      purchaseId: details.purchaseId,
      purchaseNumber: details.purchaseNumber,
      purchaseStatus: details.purchaseStatus,
      convertedAt: details.convertedAt,
    })
  return conversion?.purchaseId || conversion?.purchaseNumber ? conversion : null
}

export function extractUnresolvedManualLineLabels(err: ZadApiError): string[] {
  const details = asRecord(err.details)
  const list = firstDefined(
    details.unresolvedLines,
    details.manualLines,
    details.lines,
    details.items,
    err.errors
  )
  if (!Array.isArray(list)) return []
  return list
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      const raw = asRecord(item)
      return asText(firstDefined(raw.description, raw.designation, raw.name, raw.reference))
    })
    .filter(Boolean)
}

export function extractZadProcurementDocument(value: unknown): ZadProcurementDocument | null {
  const raw = asRecord(value)
  const inner = asRecord(raw.data)
  const nested = asRecord(firstDefined(raw.document, raw.procurementDocument, inner.document))
  const payload =
    nested._id || nested.number || nested.documentType
      ? nested
      : inner._id || inner.documentType
      ? inner
      : raw
  const id = asText(firstDefined(payload._id, payload.id))
  if (!id) return null
  const supplier = extractSupplierRef(
    firstDefined(payload.supplier, payload.supplierId, payload.registeredSupplier)
  )
  const itemsRaw = firstDefined(payload.items, payload.lines)
  const attachmentsRaw = firstDefined(payload.attachments, payload.files)
  return {
    _id: id,
    number: asText(
      firstDefined(payload.number, payload.documentNumber, payload.rfqNumber, payload.purchaseOrderNumber)
    ),
    documentType: asDocumentType(payload.documentType),
    status: asStatus(payload.status),
    recipientMode: asRecipientMode(payload.recipientMode),
    supplier,
    recipientSnapshot: extractRecipientSnapshot(
      firstDefined(payload.recipientSnapshot, payload.manualRecipient, payload.recipient),
      supplier
    ),
    documentDate: asText(firstDefined(payload.documentDate, payload.date, payload.createdAt)) || undefined,
    supplierNote: asText(firstDefined(payload.supplierNote, payload.notes)),
    internalNote: asText(payload.internalNote),
    items: Array.isArray(itemsRaw)
      ? itemsRaw.map(extractLine).filter((line): line is ZadProcurementLine => Boolean(line))
      : [],
    subtotal: asNullableNumber(firstDefined(payload.subtotal, payload.total)),
    attachments: Array.isArray(attachmentsRaw)
      ? attachmentsRaw
          .map(extractAttachment)
          .filter((item): item is ZadProcurementAttachment => Boolean(item))
      : [],
    quote: extractQuote(payload.quote),
    conversion: extractConversion(
      firstDefined(
        payload.conversion,
        payload.purchase
          ? {
              isConverted: true,
              purchase: payload.purchase,
              purchaseId: payload.purchaseId,
              purchaseNumber: payload.purchaseNumber,
              convertedAt: payload.convertedAt,
            }
          : undefined
      )
    ),
    createdAt: asText(payload.createdAt) || undefined,
    updatedAt: asText(payload.updatedAt) || undefined,
  }
}

export function extractZadProcurementList(data: unknown): {
  items: ZadProcurementDocument[]
  total: number
  totalPages: number
} {
  const raw = asRecord(data)
  const inner = asRecord(raw.data)
  const payload = inner.items || inner.documents || inner.procurementDocuments ? inner : raw
  const list = firstDefined(payload.items, payload.documents, payload.procurementDocuments)
  const items = Array.isArray(list)
    ? list
        .map(extractZadProcurementDocument)
        .filter((item): item is ZadProcurementDocument => Boolean(item))
    : []
  return {
    items,
    total: asNumberOrZero(payload.total),
    totalPages: asNumberOrZero(payload.totalPages),
  }
}

function documentFromProcurementPrint(value: unknown): ZadProcurementDocument | null {
  const print = asRecord(value)
  const number = scalarText(
    firstDefined(print.documentNumber, print.number, print.rfqNumber, print.purchaseOrderNumber)
  )
  const id = scalarText(firstDefined(print._id, print.id)) || number
  if (!id) return null
  const typeText = scalarText(firstDefined(print.documentType, print.title)).toUpperCase()
  const documentType: ZadProcurementDocumentType =
    typeText === 'PURCHASE_ORDER' || typeText === 'PO' || typeText === 'BC' || typeText.includes('COMMANDE')
      ? 'PURCHASE_ORDER'
      : 'RFQ'
  const itemsRaw = print.items
  return {
    _id: id,
    number,
    documentType,
    status: asStatus(print.status),
    recipientMode: asRecipientMode(print.recipientMode),
    recipientSnapshot: extractRecipientSnapshot(firstDefined(print.recipientSnapshot, print.recipient), null),
    documentDate: scalarText(firstDefined(print.documentDate, print.date)) || undefined,
    supplierNote: scalarText(print.supplierNote),
    internalNote: '',
    items: Array.isArray(itemsRaw)
      ? itemsRaw
          .map((item) => {
            const row = asRecord(item)
            return extractLine({
              lineType: row.lineType,
              description: firstDefined(row.description, row.designation, row.name, row.productName),
              reference: firstDefined(row.reference, row.productCode, row.code),
              barcode: row.barcode,
              quantity: row.quantity,
              unitPrice: row.unitPrice,
              subtotal: firstDefined(row.subtotal, row.lineSubtotal),
              note: row.note,
            })
          })
          .filter((line): line is ZadProcurementLine => Boolean(line))
      : [],
    subtotal: documentType === 'PURCHASE_ORDER' ? asNullableNumber(print.subtotal) : null,
    attachments: [],
  }
}

export function extractZadProcurementPrintDto(data: unknown): ZadProcurementPrintDto | null {
  const raw = asRecord(data)
  const inner = asRecord(raw.data)
  const payload = inner.print || inner.document ? inner : raw
  const printNode = asRecord(firstDefined(payload.print, payload.document, payload))
  const nested = asRecord(printNode.document)
  const document =
    nested._id || nested.id
      ? extractZadProcurementDocument(nested)
      : printNode._id || printNode.id
        ? extractZadProcurementDocument(printNode)
        : documentFromProcurementPrint(printNode)
  if (!document) return null
  const issuerRaw = firstDefined(
    printNode.issuer,
    payload.issuer,
    payload.issuerSnapshot,
    payload.issuerProfile
  )
  return {
    document: {...document, internalNote: ''},
    issuer: issuerRaw && typeof issuerRaw === 'object' ? (issuerRaw as ZadIssuerProfile) : null,
  }
}

let lineSeq = 0
export function createProcurementLineId(): string {
  lineSeq += 1
  return `line-${Date.now()}-${lineSeq}`
}

export function emptyZadProcurementEditorValues(
  documentType: ZadProcurementDocumentType = 'RFQ'
): ZadProcurementEditorValues {
  return {
    documentType,
    recipientMode: 'REGISTERED_SUPPLIER',
    supplier: '',
    recipientName: '',
    recipientAddress: '',
    recipientCity: '',
    recipientPhone: '',
    recipientEmail: '',
    recipientTaxId: '',
    supplierNote: '',
    internalNote: '',
    items: [],
  }
}

export function editorLineFromProduct(product: ZadProduct, quantity = 1): ZadProcurementEditorLine {
  const previous =
    typeof product.purchasePrice === 'number' && Number.isFinite(product.purchasePrice)
      ? product.purchasePrice
      : null
  return {
    clientId: createProcurementLineId(),
    lineType: 'PRODUCT',
    productId: product._id,
    description: product.name,
    reference: product.code,
    barcode: product.barcode || '',
    quantity: String(quantity),
    unitPrice: '',
    previousPurchasePrice: previous,
    note: '',
  }
}

export function editorLineFromManual(): ZadProcurementEditorLine {
  return {
    clientId: createProcurementLineId(),
    lineType: 'MANUAL',
    description: '',
    reference: '',
    quantity: '1',
    unitPrice: '',
    note: '',
  }
}

export function editorLinesFromReplenishment(
  items: ZadReplenishmentSelectionItem[]
): ZadProcurementEditorLine[] {
  return items.map((item) => ({
    clientId: createProcurementLineId(),
    lineType: 'PRODUCT' as const,
    productId: item.productId,
    description: item.name,
    reference: item.code,
    quantity: String(item.remainingToOrder),
    unitPrice: '',
    note: '',
  }))
}

export const ZAD_PROCUREMENT_SMART_PREFILL_QUERY: ZadReplenishmentQuery = {
  search: '',
  status: '',
  coverageStatus: '',
  needsOrdering: true,
  includeDisabled: false,
  pageNumber: 1,
  pageCount: 100,
  sortField: '',
  sortOrder: '',
}

export function shouldLoadAutomaticReplenishmentSuggestions(options: {
  isNew: boolean
  hasExplicitPrefill: boolean
}): boolean {
  return options.isNew && !options.hasExplicitPrefill
}

export function procurementQuantityFromReplenishment(
  row: Pick<ZadReplenishmentRow, 'remainingToOrder' | 'suggestedQuantity'>
): number {
  return row.remainingToOrder
}

export function editorLineFromReplenishmentSuggestion(
  row: ZadReplenishmentRow
): ZadProcurementEditorLine {
  return {
    clientId: createProcurementLineId(),
    lineType: 'PRODUCT',
    productId: row.productId,
    description: row.name,
    reference: row.code,
    barcode: row.barcode || '',
    quantity: String(procurementQuantityFromReplenishment(row)),
    unitPrice: '',
    note: '',
    fromReplenishmentSuggestion: true,
    replenishment: {
      currentStock: row.currentStock,
      onOrderQuantity: row.onOrderQuantity,
      projectedStock: row.projectedStock,
      targetStock: row.targetStock,
      remainingToOrder: row.remainingToOrder,
      status: row.status,
      coverageStatus: row.coverageStatus,
    },
  }
}

export function replenishmentRowsToSuggestionLines(
  rows: ZadReplenishmentRow[]
): ZadProcurementEditorLine[] {
  return rows
    .filter((row) => isZadReplenishmentSelectable(row) && row.remainingToOrder > 0)
    .map(editorLineFromReplenishmentSuggestion)
}

export function toZadProcurementEditorValues(
  document: ZadProcurementDocument
): ZadProcurementEditorValues {
  return {
    documentType: document.documentType,
    recipientMode: isManualRecipientMode(document.recipientMode)
      ? 'MANUAL_RECIPIENT'
      : 'REGISTERED_SUPPLIER',
    supplier: getZadProcurementSupplierId(document.supplier),
    recipientName: document.recipientSnapshot.name,
    recipientAddress: document.recipientSnapshot.address || '',
    recipientCity: document.recipientSnapshot.city || '',
    recipientPhone: document.recipientSnapshot.phone || '',
    recipientEmail: document.recipientSnapshot.email || '',
    recipientTaxId: document.recipientSnapshot.taxId || '',
    supplierNote: document.supplierNote,
    internalNote: document.internalNote,
    items: document.items.map((item) => ({
      clientId: item._id || createProcurementLineId(),
      lineId: item._id,
      lineType: item.lineType,
      productId: item.productId,
      description: item.description,
      reference: item.reference,
      barcode: item.barcode,
      quantity: String(item.quantity),
      unitPrice: item.unitPrice == null ? '' : roundZadMoney3(item.unitPrice).toFixed(3),
      previousPurchasePrice: item.previousPurchasePrice ?? null,
      note: item.note,
    })),
  }
}

export function parseZadProcurementQuantity(
  value: string
): {ok: true; value: number} | {ok: false} {
  const trimmed = String(value || '').trim()
  if (!/^[1-9]\d*$/.test(trimmed)) return {ok: false}
  return {ok: true, value: Number(trimmed)}
}

export function hasDuplicateProductLine(
  items: ZadProcurementEditorLine[],
  productId: string
): boolean {
  return items.some((item) => item.lineType === 'PRODUCT' && item.productId === productId)
}

export function previewProcurementLineSubtotal(line: ZadProcurementEditorLine): number | null {
  const quantity = parseZadProcurementQuantity(line.quantity)
  const price = parseZadPriceInput(line.unitPrice)
  if (!quantity.ok || !price.ok || line.unitPrice.trim() === '') return null
  return roundZadMoney3(quantity.value * price.value)
}

export function previewProcurementSubtotal(items: ZadProcurementEditorLine[]): number | null {
  if (!items.length) return null
  let total = 0
  for (const item of items) {
    const subtotal = previewProcurementLineSubtotal(item)
    if (subtotal == null) return null
    total += subtotal
  }
  return roundZadMoney3(total)
}

export function allProcurementLinesHavePrices(items: ZadProcurementEditorLine[]): boolean {
  return items.length > 0 && items.every((item) => previewProcurementLineSubtotal(item) != null)
}

export function validateZadProcurementEditor(values: ZadProcurementEditorValues): {
  ok: boolean
  errorId?: string
} {
  if (!isManualRecipientMode(values.recipientMode) && !values.supplier.trim()) {
    return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_SUPPLIER_REQUIRED'}
  }
  if (isManualRecipientMode(values.recipientMode) && !values.recipientName.trim()) {
    return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_RECIPIENT_NAME'}
  }
  if (!values.items.length) {
    return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_ITEMS_REQUIRED'}
  }
  const productIds = new Set<string>()
  for (const item of values.items) {
    if (!parseZadProcurementQuantity(item.quantity).ok) {
      return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_QUANTITY'}
    }
    if (item.unitPrice.trim()) {
      if (!parseZadPriceInput(item.unitPrice, {required: true}).ok) {
        return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_PRICE'}
      }
    }
    if (item.lineType === 'PRODUCT') {
      if (!item.productId) return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_PRODUCT'}
      if (productIds.has(item.productId)) {
        return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_DUPLICATE_PRODUCT'}
      }
      productIds.add(item.productId)
    } else if (!item.description.trim()) {
      return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_DESCRIPTION'}
    }
  }
  return {ok: true}
}

export function sanitizeZadProcurementWritePayload(
  values: ZadProcurementEditorValues
): ZadProcurementWritePayload {
  const items: ZadProcurementLineWritePayload[] = values.items.map((item) => {
    const quantity = parseZadProcurementQuantity(item.quantity)
    const price = item.unitPrice.trim() ? parseZadPriceInput(item.unitPrice) : {ok: true as const, value: null as number | null}
    const unitPrice =
      item.unitPrice.trim() && price.ok && price.value != null ? price.value : undefined
    if (item.lineType === 'PRODUCT') {
      const payload: ZadProcurementLineWritePayload = {
        lineType: 'PRODUCT',
        productId: item.productId,
        quantity: quantity.ok ? quantity.value : 0,
        note: item.note.trim() || undefined,
      }
      if (unitPrice != null) payload.unitPrice = unitPrice
      return payload
    }
    const payload: ZadProcurementLineWritePayload = {
      lineType: 'MANUAL',
      description: item.description.trim(),
      reference: item.reference.trim() || undefined,
      quantity: quantity.ok ? quantity.value : 0,
      note: item.note.trim() || undefined,
    }
    if (unitPrice != null) payload.unitPrice = unitPrice
    return payload
  })

  if (!isManualRecipientMode(values.recipientMode)) {
    return {
      documentType: values.documentType,
      recipientMode: 'REGISTERED_SUPPLIER',
      supplierId: values.supplier.trim(),
      supplierNote: values.supplierNote.trim() || undefined,
      internalNote: values.internalNote.trim() || undefined,
      items,
    }
  }
  return {
    documentType: values.documentType,
    recipientMode: 'MANUAL_RECIPIENT',
    recipientSnapshot: {
      name: values.recipientName.trim(),
      address: values.recipientAddress.trim() || undefined,
      city: values.recipientCity.trim() || undefined,
      phone: values.recipientPhone.trim() || undefined,
      email: values.recipientEmail.trim() || undefined,
      taxId: values.recipientTaxId.trim() || undefined,
    },
    supplierNote: values.supplierNote.trim() || undefined,
    internalNote: values.internalNote.trim() || undefined,
    items,
  }
}

export function canEditZadProcurement(status?: ZadProcurementStatus): boolean {
  return status === 'DRAFT' || !status
}

export function canSendZadRfq(document: Pick<ZadProcurementDocument, 'documentType' | 'status'>): boolean {
  return document.documentType === 'RFQ' && document.status === 'DRAFT'
}

export function canQuoteZadRfq(document: Pick<ZadProcurementDocument, 'documentType' | 'status'>): boolean {
  return document.documentType === 'RFQ' && document.status === 'SENT'
}

export function canConfirmZadPurchaseOrder(
  document: Pick<ZadProcurementDocument, 'documentType' | 'status'>
): boolean {
  return document.documentType === 'PURCHASE_ORDER' && document.status === 'DRAFT'
}

export function canSendZadPurchaseOrder(
  document: Pick<ZadProcurementDocument, 'documentType' | 'status'>
): boolean {
  return document.documentType === 'PURCHASE_ORDER' && document.status === 'CONFIRMED'
}

export function canCancelZadProcurement(status?: ZadProcurementStatus): boolean {
  return Boolean(status && status !== 'CANCELLED')
}

export function isZadProcurementConvertStatusEligible(
  document: Pick<ZadProcurementDocument, 'documentType' | 'status' | 'conversion'>
): boolean {
  if (document.documentType !== 'PURCHASE_ORDER') return false
  if (document.status !== 'CONFIRMED' && document.status !== 'SENT') return false
  if (document.conversion?.isConverted) return false
  return true
}

export function zadProcurementConvertBlockers(document: ZadProcurementDocument): {
  eligible: boolean
  showAction: boolean
  manualRecipient: boolean
  manualLines: boolean
} {
  const eligible = isZadProcurementConvertStatusEligible(document)
  const manualRecipient = eligible && isManualRecipientMode(document.recipientMode)
  const manualLines = eligible && document.items.some((item) => item.lineType === 'MANUAL')
  return {
    eligible,
    showAction: eligible && !manualRecipient && !manualLines,
    manualRecipient,
    manualLines,
  }
}

export function zadAssociatedPurchasePath(purchaseId?: string): string | null {
  return purchaseId ? `/zad/purchases/${purchaseId}` : null
}

export function associatedPurchaseStatusI18n(status?: string): string | null {
  if (status === 'DRAFT') return 'ZAD.PURCHASES.STATUS_DRAFT'
  if (status === 'VALIDATED') return 'ZAD.PURCHASES.STATUS_VALIDATED'
  if (status === 'CANCELLED') return 'ZAD.PURCHASES.STATUS_CANCELLED'
  return null
}

export function associatedPurchaseStockMessageId(status?: string): string | null {
  if (status === 'DRAFT') return 'ZAD.PROCUREMENT.CONVERT_STOCK_UNCHANGED'
  if (status === 'VALIDATED') return 'ZAD.PROCUREMENT.CONVERT_STOCK_UPDATED'
  if (status === 'CANCELLED') return 'ZAD.PROCUREMENT.CONVERT_PURCHASE_CANCELLED'
  return null
}

export function canChangeZadProcurementAttachments(
  document: Pick<ZadProcurementDocument, 'documentType' | 'status'>
): boolean {
  if (document.documentType === 'RFQ') return document.status === 'DRAFT' || document.status === 'SENT'
  return document.status === 'DRAFT'
}

export function rfqNeverCountsAsOnOrder(): boolean {
  return true
}

export function purchaseOrderCountsAsOnOrder(status?: ZadProcurementStatus): boolean {
  return status === 'CONFIRMED' || status === 'SENT'
}

export function shouldRefetchReplenishmentAfterStatus(
  documentType: ZadProcurementDocumentType,
  status: string
): boolean {
  if (documentType !== 'PURCHASE_ORDER') return false
  return status === 'CONFIRMED' || status === 'SENT' || status === 'CANCELLED'
}

export function activeProcurementAttachments(
  attachments: ZadProcurementAttachment[]
): ZadProcurementAttachment[] {
  return attachments.filter((item) => item.isActive !== false)
}

export function validateZadProcurementAttachment(file: File, currentCount: number): {
  ok: boolean
  errorId?: string
} {
  if (currentCount >= ZAD_PROCUREMENT_MAX_ATTACHMENTS) {
    return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_ATTACHMENT_LIMIT'}
  }
  if (file.size > ZAD_PROCUREMENT_MAX_ATTACHMENT_BYTES) {
    return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_ATTACHMENT_SIZE'}
  }
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  const mimeOk = ZAD_PROCUREMENT_ATTACHMENT_MIME.includes(file.type)
  const extOk = ZAD_PROCUREMENT_ATTACHMENT_EXTENSIONS.includes(ext)
  if (!mimeOk && !extOk) {
    return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_ATTACHMENT_TYPE'}
  }
  return {ok: true}
}

export function buildZadProcurementQuotePayload(
  document: ZadProcurementDocument,
  quoteReference: string,
  quoteDate: string,
  prices: Record<string, string>
): {ok: true; payload: ZadProcurementQuotePayload} | {ok: false; errorId: string} {
  const lines: ZadProcurementQuotePayload['lines'] = []
  for (const item of document.items) {
    const key = item._id || item.productId || item.description
    const parsed = parseZadPriceInput(prices[key], {required: true})
    if (!parsed.ok) return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_QUOTE_PRICE'}
    lines.push({
      lineId: item._id,
      productId: item.productId,
      quantity: item.quantity,
      unitPrice: parsed.value,
    })
  }
  if (!quoteReference.trim()) return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_QUOTE_REFERENCE'}
  if (!quoteDate.trim()) return {ok: false, errorId: 'ZAD.PROCUREMENT.ERR_QUOTE_DATE'}
  return {
    ok: true,
    payload: {
      quoteReference: quoteReference.trim(),
      quoteDate: quoteDate.trim(),
      lines,
    },
  }
}

export function formatZadProcurementStatusI18n(
  documentType: ZadProcurementDocumentType,
  status: ZadProcurementStatus
): string {
  if (documentType === 'RFQ') {
    if (status === 'SENT') return 'ZAD.PROCUREMENT.STATUS_RFQ_SENT'
    if (status === 'QUOTED') return 'ZAD.PROCUREMENT.STATUS_QUOTED'
    if (status === 'CANCELLED') return 'ZAD.PROCUREMENT.STATUS_RFQ_CANCELLED'
    return 'ZAD.PROCUREMENT.STATUS_DRAFT'
  }
  if (status === 'CONFIRMED') return 'ZAD.PROCUREMENT.STATUS_CONFIRMED'
  if (status === 'SENT') return 'ZAD.PROCUREMENT.STATUS_PO_SENT'
  if (status === 'CANCELLED') return 'ZAD.PROCUREMENT.STATUS_PO_CANCELLED'
  return 'ZAD.PROCUREMENT.STATUS_DRAFT'
}

export function formatZadProcurementTypeI18n(documentType: ZadProcurementDocumentType): string {
  return documentType === 'PURCHASE_ORDER' ? 'ZAD.PROCUREMENT.TYPE_PO' : 'ZAD.PROCUREMENT.TYPE_RFQ'
}

export function resolveZadProcurementMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  if (err.errorCode === 'REGISTERED_SUPPLIER_REQUIRED') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_REGISTERED_SUPPLIER',
      fallback: 'A registered supplier is required.',
    }
  }
  if (err.errorCode === 'SUPPLIER_INACTIVE') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_SUPPLIER_INACTIVE',
      fallback: 'This supplier is inactive.',
    }
  }
  if (err.errorCode === 'MANUAL_LINES_REQUIRE_PRODUCT_MAPPING') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_MANUAL_MAPPING',
      fallback: 'Manual lines require product mapping before conversion.',
    }
  }
  if (err.errorCode === 'ALREADY_CONVERTED') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_ALREADY_CONVERTED',
      fallback: 'This purchase order already has an associated purchase.',
    }
  }
  if (err.errorCode === 'RFQ_NOT_CONVERTIBLE') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_RFQ_NOT_CONVERTIBLE',
      fallback: 'A request for quotation cannot be converted into a purchase.',
    }
  }
  if (err.errorCode === 'NOT_CONVERTIBLE') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_NOT_CONVERTIBLE',
      fallback: 'This purchase order cannot be converted in its current status.',
    }
  }
  if (err.errorCode === 'INVALID_TRANSITION') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_TRANSITION',
      fallback: 'This status change is not allowed.',
    }
  }
  if (err.errorCode === 'INVALID_LINE') {
    return {i18nId: 'ZAD.PROCUREMENT.ERR_LINE', fallback: 'A document line is invalid.'}
  }
  if (err.errorCode === 'ATTACHMENT_INVALID' || err.errorCode === 'ATTACHMENT_LIMIT') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_ATTACHMENT',
      fallback: 'The attachment is not valid.',
    }
  }
  if (err.errorCode === 'PROCUREMENT_NOT_EDITABLE' || err.errorCode === 'DOCUMENT_NOT_EDITABLE') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_NOT_EDITABLE',
      fallback: 'Commercial content can no longer be edited.',
    }
  }
  if (err.errorCode === 'DUPLICATE_PRODUCT') {
    return {
      i18nId: 'ZAD.PROCUREMENT.ERR_DUPLICATE_PRODUCT',
      fallback: 'This product is already present in the document.',
    }
  }
  return resolveZadUserMessage(err)
}

export function supplierPickerLabel(supplier: ZadSupplier): string {
  return `${supplier.name} — ${supplier.code}`
}

export function displayProcurementAmount(
  document: Pick<ZadProcurementDocument, 'documentType' | 'subtotal' | 'items'>
): number | null {
  if (document.subtotal != null && Number.isFinite(document.subtotal)) {
    const hasUnknown = document.items.some((item) => item.unitPrice == null)
    if (document.documentType === 'RFQ' && hasUnknown) return null
    return document.subtotal
  }
  if (document.items.some((item) => item.unitPrice == null)) return null
  if (!document.items.length) return null
  return roundZadMoney3(
    document.items.reduce((sum, item) => sum + (item.subtotal || 0), 0)
  )
}
